import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@sea-snag/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { useOrganization } from '../../contexts/organization-context';
import { useConnect, useSetupStatus } from '../../hooks/use-setup';
import type { CompanyOption } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';
import { setOrganization } from '../../stores/org-store';
import { SetupCompanyStep } from './setup-company-step';
import { SetupKeyStep } from './setup-key-step';
import { SetupProgressStep } from './setup-progress-step';

type Step = 'key' | 'company' | 'progress';

interface SetupModalProps {
  orgPid: string;
}

export function SetupModal({ orgPid }: SetupModalProps) {
  const [step, setStep] = useState<Step>('key');
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [apiKey, setApiKey] = useState('');
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const connect = useConnect(orgPid);
  const { data: status } = useSetupStatus(orgPid);
  const reconnectChecked = useRef(false);

  // Reconnection: if sync is already running or failed, jump to progress step
  useEffect(() => {
    if (reconnectChecked.current || !status) return;
    reconnectChecked.current = true;

    if (status.syncState?.running) {
      setStep('progress');
    } else if (status.integration?.status === 'error') {
      setStep('progress');
    }
  }, [status]);

  function handleValidated(validatedCompanies: CompanyOption[], validatedKey: string) {
    setApiKey(validatedKey);
    setCompanies(validatedCompanies);

    // Auto-connect if only one company
    if (validatedCompanies.length === 1) {
      const company = validatedCompanies[0];
      if (!company) return;
      connect.mutate(
        {
          provider: 'mmk',
          apiKey: validatedKey,
          companyExternalId: company.id,
        },
        {
          onSuccess: () => setStep('progress'),
        }
      );
      return;
    }

    setStep('company');
  }

  function handleConnected() {
    setStep('progress');
  }

  function handleComplete() {
    // Update org store so the onboarding gate closes immediately
    if (organization) {
      setOrganization({ ...organization, setupCompletedAt: new Date().toISOString() });
    }
    void queryClient.invalidateQueries({ queryKey: queryKeys.setup.status(orgPid) });
  }

  return (
    <Dialog open>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-md"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Fleet Setup</DialogTitle>
          <DialogDescription>
            {step === 'key' && 'Connect your booking manager to get started.'}
            {step === 'company' && 'Select which company to sync.'}
            {step === 'progress' && 'Syncing your fleet data...'}
          </DialogDescription>
        </DialogHeader>

        {step === 'key' && (
          <SetupKeyStep
            orgPid={orgPid}
            onValidated={handleValidated}
            isConnecting={connect.isPending}
            connectError={connect.error instanceof Error ? connect.error : null}
          />
        )}
        {step === 'company' && (
          <SetupCompanyStep
            orgPid={orgPid}
            companies={companies}
            apiKey={apiKey}
            onConnected={handleConnected}
          />
        )}
        {step === 'progress' && <SetupProgressStep orgPid={orgPid} onComplete={handleComplete} />}
      </DialogContent>
    </Dialog>
  );
}
